import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue'

import { useMutation, useQuery } from '@tanstack/vue-query'
import { useClipboard } from '@vueuse/core'
import { useRouter } from 'vue-router'

import { handleApiErrorToast } from '@/apiClient'
import { WebsitesApi } from '@/apiClient/modules/websites'
import {
  chatExistsQueryOptions,
  chatWebsiteQueryKey,
  fetchChatExists,
} from '@/apiClient/queries/useChatQueries'
import type { IWebsiteSummary } from '@/apiClient/types/websites'
import { websitePreviewIntentQuery } from '@/composables/useChatWebsitePreviewIntent'
import { useMetricEvents } from '@/composables/useMetricEvents'
import { queryClient } from '@/lib/queryClient'
import { useToast } from '@/store/toast'
import { downloadTextFile, ensureHtmlExtension } from '@/utils/downloadFile'
import { fetchWebsiteFileHtml } from '@/utils/fetchWebsiteFileHtml'
import { websitePublicHref } from '@/utils/websiteSlug'
import type { IMenuDropdownItem } from '@/views/components/ui/dropdowns/UIMenuDropdown.vue'

export type WebsiteCardStatus = 'published' | 'unpublished' | 'draft'
export type WebsiteCardActionsVariant = 'library' | 'preview-toolbar'

export type WebsiteCardMenuValue =
  | 'edit'
  | 'edit-content'
  | 'publish'
  | 'unpublish'
  | 'download'
  | 'delete'
  | 'divider'

/**
 * A website-card menu row. Reuses {@link IMenuDropdownItem}'s shape so it stays in
 * sync with the UIMenuDropdown contract, narrowing `value` to the actions this card
 * dispatches (via the dropdown's `select` event) on.
 */
export type WebsiteCardMenuItem = Omit<IMenuDropdownItem, 'value'> & {
  value: WebsiteCardMenuValue
}

/** Websites predating source-chat tracking carry no chat ref at all. */
const EDIT_CONTENT_NO_SOURCE_TOOLTIP =
  'Edit content is not supported for deprecated or older-version websites'

/** The website outlived its chat: chats are soft-deleted by their owner or by the
 *  backend's retention sweep, and neither takes the published page down with it. */
const EDIT_CONTENT_MISSING_CHAT_TOOLTIP =
  'The chat this webpage was generated from no longer exists'

/** Shown while the source-chat probe is still out — the row can't say yet whether
 *  it leads anywhere. */
const EDIT_CONTENT_CHECKING_TOOLTIP =
  'Checking the chat this webpage was generated from'

export function deriveCardStatus(website: IWebsiteSummary): WebsiteCardStatus {
  if (website.is_published) return 'published'
  if (website.link?.trim()) return 'unpublished'
  return 'draft'
}

export function buildMenuItems(
  variant: WebsiteCardActionsVariant,
  status: WebsiteCardStatus,
  options: {
    hasSourceChat?: boolean
    isSourceChatMissing?: boolean
    isSourceChatChecking?: boolean
  } = {},
): WebsiteCardMenuItem[] {
  if (variant === 'preview-toolbar') {
    // The preview toolbar surfaces this menu only for the live, published chat page,
    // so every row is a published-state action. "Edit settings" re-opens the publish
    // modal in update mode (name / slug / visibility / password).
    return [
      { label: 'Edit settings', value: 'edit', icon: 'sliders' },
      { label: 'Unpublish', value: 'unpublish', icon: 'unpublished' },
      {
        label: 'Download HTML',
        value: 'download',
        icon: 'arrowDownToLineRegular',
      },
      { label: '', value: 'divider', isDisabled: true },
      {
        label: 'Delete',
        value: 'delete',
        icon: 'trashRegular',
        variant: 'destructive',
      },
    ]
  }

  const items: WebsiteCardMenuItem[] = []

  // "Edit content" (EVGPT-1546) jumps back to the chat this HTML was generated in so the
  // user can edit + re-generate it. It's offered on every library card so the action stays
  // discoverable, and is enabled whenever the website carries a `source_chat_id` that
  // still resolves — regardless of publish state (published, unpublished, or draft).
  // Two ways it can't: assets predating source-chat tracking carry no ref at all, and a
  // chat that has since been deleted leaves the ref dangling. Either way the row is shown
  // disabled with the reason as a tooltip, rather than silently hidden.
  // A third, transient way: the probe that answers "does that chat still exist?" is still
  // out. Rather than let a click go through and be answered a beat later by an error toast
  // (or a navigation into a chat that turns out to be gone), the row spins and stays
  // disabled until the answer lands.
  const isCheckingSourceChat =
    Boolean(options.hasSourceChat) && Boolean(options.isSourceChatChecking)
  const canEditContent =
    Boolean(options.hasSourceChat) &&
    !options.isSourceChatMissing &&
    !isCheckingSourceChat
  items.push({
    label: 'Edit content',
    value: 'edit-content',
    icon: 'pencil',
    isLoading: isCheckingSourceChat,
    isDisabled: !canEditContent,
    tooltip: canEditContent
      ? undefined
      : isCheckingSourceChat
        ? EDIT_CONTENT_CHECKING_TOOLTIP
        : options.hasSourceChat
          ? EDIT_CONTENT_MISSING_CHAT_TOOLTIP
          : EDIT_CONTENT_NO_SOURCE_TOOLTIP,
  })

  if (status === 'published') {
    items.push(
      { label: 'Edit settings', value: 'edit', icon: 'sliders' },
      { label: 'Unpublish', value: 'unpublish', icon: 'unpublished' },
    )
  } else {
    // Unpublished / draft: the only publish action offered is to publish it.
    items.push({
      label: 'Publish to Web',
      value: 'publish',
      icon: 'published',
    })
  }

  items.push(
    {
      label: 'Download HTML',
      value: 'download',
      icon: 'arrowDownToLineRegular',
    },
    { label: '', value: 'divider', isDisabled: true },
    {
      label: 'Delete',
      value: 'delete',
      icon: 'trashRegular',
      variant: 'destructive',
    },
  )

  return items
}

export function isWebsiteCardMenuOption(option: {
  value: string
}): option is WebsiteCardMenuItem {
  return option.value !== 'divider'
}

export function useWebsiteCardActions(options: {
  variant: WebsiteCardActionsVariant
  websiteId?: MaybeRefOrGetter<string | undefined>
  website?: MaybeRefOrGetter<IWebsiteSummary | undefined>
  publishedHref?: MaybeRefOrGetter<string>
  htmlContent?: MaybeRefOrGetter<string>
  downloadFileName?: MaybeRefOrGetter<string | undefined>
  /**
   * "Edit settings" handler. When provided it runs instead of opening the local
   * `showPublishModal` — the preview toolbar has no local publish modal and bubbles the
   * request up to the preview panel instead. The library card omits it and falls back
   * to its own `ModalPublishToWeb`.
   */
  onEdit?: () => void
  onUnpublished?: () => void
  onDeleted?: () => void
}) {
  const { copy } = useClipboard()
  const router = useRouter()
  const showDeleteModal = ref(false)
  const showUnpublishModal = ref(false)
  const showPublishModal = ref(false)
  const isDownloading = ref(false)
  const hasOpenedMenu = ref(false)

  const websiteRef = computed(() => toValue(options.website))
  const websiteIdRef = computed(() => {
    if (options.websiteId !== undefined) {
      return toValue(options.websiteId) ?? ''
    }
    return websiteRef.value?.id ?? ''
  })

  const sourceChatId = computed(() => websiteRef.value?.source_chat_id ?? '')

  /** Where the action was taken, for analytics. */
  const eventSurface = options.variant === 'library' ? 'library' : 'preview'
  const sourceMessageId = computed(
    () => websiteRef.value?.source_message_id ?? '',
  )

  /**
   * Does the "Edit content" target still exist? The list payload only proves the
   * website *remembers* a chat, not that the chat is still there — chats are deleted
   * by their owner or aged out by retention, and neither touches the website.
   *
   * Deferred until the menu is first opened: an eager check would cost one request per
   * card on the Library grid, and the answer is only ever needed by a row inside the
   * menu. Cached per chat id for the session, so re-opening this card — or any other
   * card published from the same chat — is free.
   */
  const sourceChatExistsQuery = useQuery(() => ({
    // Same options object {@link fetchChatExists} fetches with, so this observer and
    // the click-time check share one cache entry rather than probing twice.
    ...chatExistsQueryOptions(sourceChatId.value),
    enabled:
      options.variant === 'library' &&
      hasOpenedMenu.value &&
      Boolean(sourceChatId.value),
  }))

  /** Only a probe that came back negative disables the row for good. A probe that failed
   *  for any reason other than a 404 leaves it enabled — the chat may well be there — and
   *  {@link onEditContent} makes the call again at click time. */
  const isSourceChatMissing = computed(
    () => sourceChatExistsQuery.data.value === false,
  )

  /** True only while the first probe for this chat is in flight (vue-query's `isLoading`
   *  is `isPending && isFetching`, so it stays false both before the menu arms the query
   *  and once an answer — from this card or any other card published from the same chat —
   *  is cached). Holds the row in a spinning, unclickable state for that window. */
  const isSourceChatChecking = computed(
    () => sourceChatExistsQuery.isLoading.value,
  )

  const cardStatus = computed((): WebsiteCardStatus => {
    const website = websiteRef.value
    if (!website) return 'draft'
    return deriveCardStatus(website)
  })

  const publishedHref = computed(() => {
    if (options.publishedHref !== undefined) {
      return toValue(options.publishedHref) ?? ''
    }
    const website = websiteRef.value
    if (!website || cardStatus.value !== 'published' || !website.link) return ''
    return websitePublicHref(website.link)
  })

  const statusLabel = computed(() => {
    switch (cardStatus.value) {
      case 'published':
        return 'Published'
      case 'unpublished':
        return 'Unpublished'
      default:
        return 'Draft'
    }
  })

  const unpublishMutation = useMutation({
    mutationFn: () =>
      WebsitesApi.update(websiteIdRef.value, { is_published: false }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['websites'] })
      // This website may be some chat's website (GET /chats/{id}/website); the card
      // doesn't know which, so refresh them all rather than leave a "published" read.
      queryClient.invalidateQueries({ queryKey: chatWebsiteQueryKey() })
      useToast().onSuccess('Webpage unpublished')
      useMetricEvents().sendEvent('website_unpublished', {
        website_id: websiteIdRef.value,
        surface: eventSurface,
      })
      showUnpublishModal.value = false
      options.onUnpublished?.()
    },
    onError: handleApiErrorToast,
  })

  const deleteMutation = useMutation({
    mutationFn: () => WebsitesApi.remove(websiteIdRef.value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['websites'] })
      // The deleted website was possibly a chat's website — refetch so that chat
      // resolves to `null` (and its next publish creates a fresh one).
      queryClient.invalidateQueries({ queryKey: chatWebsiteQueryKey() })
      const message =
        options.variant === 'library' ? 'Asset deleted' : 'Webpage deleted'
      useToast().onSuccess(message)
      showDeleteModal.value = false
      options.onDeleted?.()
    },
    onError: handleApiErrorToast,
  })

  const isBusy = computed(
    () =>
      unpublishMutation.isPending.value ||
      deleteMutation.isPending.value ||
      isDownloading.value,
  )
  const isDeleting = computed(() => deleteMutation.isPending.value)
  const isUnpublishing = computed(() => unpublishMutation.isPending.value)

  const menuItems = computed(() =>
    options.variant === 'library'
      ? buildMenuItems('library', cardStatus.value, {
          hasSourceChat: Boolean(sourceChatId.value),
          isSourceChatMissing: isSourceChatMissing.value,
          isSourceChatChecking: isSourceChatChecking.value,
        })
      : buildMenuItems('preview-toolbar', 'published'),
  )

  /**
   * Call when the card's menu trigger is clicked. Arms the source-chat probe, whose
   * answer the "Edit content" row needs — see {@link sourceChatExistsQuery} for why it
   * isn't resolved up front.
   */
  function onMenuTriggerClick() {
    hasOpenedMenu.value = true
  }

  async function onCopyLink() {
    if (!publishedHref.value) return
    try {
      await copy(publishedHref.value)
      useToast().onSuccess('Link copied')
    } catch {
      useToast().onError('Could not copy link')
    }
  }

  function onOpenPreview() {
    if (!publishedHref.value) return
    window.open(publishedHref.value, '_blank', 'noopener,noreferrer')
  }

  async function resolveHtmlContent(): Promise<string> {
    const inline = toValue(options.htmlContent)?.trim() ?? ''
    if (inline) return inline

    const websiteId = websiteIdRef.value
    if (!websiteId) return ''

    const website = await WebsitesApi.get(websiteId)
    if (!website.file) return ''

    return fetchWebsiteFileHtml(website, website.file)
  }

  async function onDownloadHtml() {
    isDownloading.value = true
    try {
      const html = (await resolveHtmlContent()).trim()
      if (!html) {
        useToast().onError('No HTML content to download.')
        return
      }

      const rawName =
        toValue(options.downloadFileName)?.trim() ||
        websiteRef.value?.name ||
        'webpage.html'

      downloadTextFile(html, ensureHtmlExtension(rawName))
      useMetricEvents().sendEvent('html_downloaded', {
        surface: eventSurface,
        website_id: websiteIdRef.value || undefined,
      })
    } catch (error) {
      handleApiErrorToast(error as Parameters<typeof handleApiErrorToast>[0])
    } finally {
      isDownloading.value = false
    }
  }

  function onRequestUnpublish() {
    if (!websiteIdRef.value) {
      useToast().onError('Website not found.')
      return
    }
    showUnpublishModal.value = true
  }

  async function onConfirmUnpublish() {
    if (!websiteIdRef.value) {
      useToast().onError('Website not found.')
      return
    }
    try {
      await unpublishMutation.mutateAsync()
    } catch {
      // The error toast is already surfaced by the mutation's `onError`; swallow
      // the rejection here so it doesn't escape as an unhandledrejection.
    }
  }

  function onRequestDelete() {
    showDeleteModal.value = true
  }

  async function onConfirmDelete() {
    if (!websiteIdRef.value) {
      useToast().onError('Website not found.')
      return
    }
    try {
      await deleteMutation.mutateAsync()
    } catch {
      // Error surfaced by the mutation's `onError`; swallow the rejection.
    }
  }

  // "Edit content": jump back to the chat this HTML was generated in so the user can
  // edit + re-generate it. Uses the source_chat_id read back on the website summary
  // (the menu row only exists when it's present, but guard anyway). The website ref
  // rides along in the query so the Chat page also opens this page in the preview
  // panel — the user lands on the page they asked to edit, not just its chat.
  async function onEditContent() {
    const chatId = sourceChatId.value
    if (!chatId) {
      useToast().onError('This webpage has no source chat to edit.')
      return
    }

    // Confirm the chat is there before routing to it: the Chat page answers a deleted
    // chat by toasting and redirecting to a new chat, which would throw the user out of
    // the Library entirely. The row is only clickable once the menu-open probe settled,
    // so this normally resolves straight from that cache entry; it still matters when the
    // probe errored (non-404), which leaves the row enabled on purpose.
    try {
      if (!(await fetchChatExists(chatId))) {
        useToast().onError(`${EDIT_CONTENT_MISSING_CHAT_TOOLTIP}.`)
        return
      }
    } catch {
      // Not a 404 — the chat may well be there, so navigate and let the Chat page
      // report whatever went wrong rather than blocking on a transient failure.
    }

    router.push({
      name: 'Chat',
      params: { id: chatId },
      query: websitePreviewIntentQuery({
        websiteId: websiteIdRef.value,
        messageId: sourceMessageId.value || undefined,
      }),
    })
  }

  function onMenuOptionClick(option: WebsiteCardMenuItem) {
    if (option.value === 'divider' || option.isDisabled) return

    switch (option.value) {
      case 'edit':
        if (options.onEdit) options.onEdit()
        else showPublishModal.value = true
        break
      case 'edit-content':
        void onEditContent()
        break
      case 'publish':
        showPublishModal.value = true
        break
      case 'unpublish':
        onRequestUnpublish()
        break
      case 'download':
        void onDownloadHtml()
        break
      case 'delete':
        onRequestDelete()
        break
    }
  }

  return {
    cardStatus,
    statusLabel,
    publishedHref,
    isBusy,
    isDeleting,
    isUnpublishing,
    showDeleteModal,
    showUnpublishModal,
    showPublishModal,
    menuItems,
    onCopyLink,
    onOpenPreview,
    onOpenLive: onOpenPreview,
    onDownloadHtml,
    onRequestUnpublish,
    onConfirmUnpublish,
    onRequestDelete,
    onConfirmDelete,
    onMenuTriggerClick,
    onMenuOptionClick,
  }
}
